// Ultimate Card Game (UCG) - Unit MVP Spotlight animation.
//
// Purely cosmetic post-game UI playback (runs after Game.isRunning is
// already false) - not part of the command-driven simulation, so unlike
// js/engine/unitMvp.js this file is free to use Math.random()/rAF/real time.
//
// Renders the match's best unit (js/engine/unitMvp.js) walking in from its
// team's battlefield side, then holding position at center and firing a
// generic sparkle burst (a stand-in for "attacking" that works identically
// for every role - DPS, healer, tank, see docs/UNIT_MVP_PLAN.md section 2.8)
// twice every 10 seconds, indefinitely, until stop() is called (main.js
// calls it when the game-over modal is reused for a Rematch).
//
// "Indefinitely" is cheap on purpose: the loop sleeps entirely while the canvas is not on screen (the
// player left the game-over modal for the Cargo Bay or the Archive - stop() only runs at the next
// match start), never runs faster than ~60 fps on 120 Hz phones, and once the unit stands still it only
// draws while sparkles fly (see needsDraw below) instead of repainting an identical frame 60-120x a
// second for as long as the stats are being read.
//
// Reuses buildSpriteBitmap() - the same procedural pixel-art generator the
// real battlefield renderer uses (js/engine/render/unitSprites.js) - so the
// sprite is authentic, not a fallback artwork image. For the handful of
// infantry-style types that have hand-built Legs/Torso/Gun bitmaps and a
// walk-frame sequence (see INFANTRY_WALK below), the legs actually cycle
// through that sequence while walking - every other unit type (vehicles,
// creatures, anything without dedicated walk frames) falls back to a single
// static bitmap with a gentle bob, since replicating every unit type's
// bespoke multi-part animation from js/engine/render/units.js is not
// realistic for a decorative highlight (see docs/UNIT_MVP_PLAN.md 8a).

import { buildSpriteBitmap } from '../engine/render/unitSprites.js';

const WALK_SPEED = 130; // px/s
const WALK_CYCLE_SPEED = 8; // rad/s, matches the leg-frame swap cadence
const IDLE_CYCLE_SECONDS = 10; // time between shooting bursts once stopped at center
const IDLE_SPARKLE_AT = [0, 0.4]; // seconds into each idle cycle when a burst fires
const BOB_AMPLITUDE = 3;
const BOB_SPEED = 9; // rad/s, only used for the no-walk-frames fallback
const MAX_DRAW_SIZE = 60;

// Unit types with hand-built Legs (+ a 6-frame walk cycle)/Torso/Gun bitmaps
// in js/engine/render/unitSprites.js - see the *_WALK_FRAMES arrays in
// js/engine/render/units.js for the canonical list this mirrors.
const INFANTRY_WALK = {
  Marine: { stand: 'Marine_Legs', walk: ['Marine_Legs_Walk1', 'Marine_Legs_Walk2', 'Marine_Legs_Walk3', 'Marine_Legs_Walk4', 'Marine_Legs_Walk5', 'Marine_Legs_Walk6'], torso: 'Marine_Torso', gun: 'Marine_Gun' },
  HyperMarine: { stand: 'HyperMarine_Legs', walk: ['HyperMarine_Legs_Walk1', 'HyperMarine_Legs_Walk2', 'HyperMarine_Legs_Walk3', 'HyperMarine_Legs_Walk4', 'HyperMarine_Legs_Walk5', 'HyperMarine_Legs_Walk6'], torso: 'HyperMarine_Torso', gun: 'HyperMarine_Gun' },
  DevilPatrol: { stand: 'DevilPatrol_Legs', walk: ['DevilPatrol_Legs_Walk1', 'DevilPatrol_Legs_Walk2', 'DevilPatrol_Legs_Walk3', 'DevilPatrol_Legs_Walk4', 'DevilPatrol_Legs_Walk5', 'DevilPatrol_Legs_Walk6'], torso: 'DevilPatrol_Torso', gun: 'DevilPatrol_Gun' },
  Shade: { stand: 'Shade_Legs', walk: ['Shade_Legs_Walk1', 'Shade_Legs_Walk2', 'Shade_Legs_Walk3', 'Shade_Legs_Walk4', 'Shade_Legs_Walk5', 'Shade_Legs_Walk6'], torso: 'Shade_Torso', gun: 'Shade_Gun' },
  HyperShade: { stand: 'HyperShade_Legs', walk: ['HyperShade_Legs_Walk1', 'HyperShade_Legs_Walk2', 'HyperShade_Legs_Walk3', 'HyperShade_Legs_Walk4', 'HyperShade_Legs_Walk5', 'HyperShade_Legs_Walk6'], torso: 'HyperShade_Torso', gun: 'HyperShade_Gun' }
};

// Sul Rejected Loaf: the static bitmap from buildSpriteBitmap() only bakes in
// the skull and the filament root anchors (js/engine/render/unitSprites.js),
// not the 5 trailing mycelium filament strands themselves - those only exist
// as per-frame procedural strokes, duplicated once for the live battlefield
// (js/engine/render/units.js) and once for the showroom preview
// (js/ui/showroomModal.js). Without a third copy here the MVP spotlight would
// show a disembodied floating skull with no tentacles at all.
const REJECTED_LOAF_STRAND_FREQ = Object.freeze([0.82, 1.12, 0.94, 1.22, 0.86]);
const REJECTED_LOAF_STRAND_PHASE = Object.freeze([0.0, 2.15, 4.30, 1.45, 3.75]);
const REJECTED_LOAF_STRAND_SPATIAL = Object.freeze([4.2, 5.4, 4.6, 5.8, 4.4]);
const REJECTED_LOAF_STRAND_LEN = Object.freeze([0.48, 0.62, 0.74, 0.58, 0.50]);
const REJECTED_LOAF_STRAND_AMP = Object.freeze([1.10, 0.88, 1.05, 0.90, 1.15]);
const REJECTED_LOAF_SEG_COLORS = Object.freeze(['#581c87', '#6b21a8', '#7e22ce', '#9333ea', '#a855f7', '#c084fc']);
const REJECTED_LOAF_SEG_WIDTHS = Object.freeze([2.6, 2.2, 1.9, 1.6, 1.3, 1.0]);
const _rejectedStrandPts = Array.from({ length: 5 }, () => Array.from({ length: 7 }, () => ({ x: 0, y: 0 })));

function drawRejectedLoafFilaments(ctx, t, skullX, skullY, drawSize) {
  const breathPulse = Math.sin(t * 1.8);
  const stretchY = 1.0 + breathPulse * 0.025;
  const squashX = 1.0 - breathPulse * 0.018;
  const bulbLocalY = drawSize * 0.20 * stretchY;
  const umbrellaFactor = breathPulse * 0.25;

  for (let s = 0; s < 5; s++) {
    const sFreq = REJECTED_LOAF_STRAND_FREQ[s];
    const sPhase = REJECTED_LOAF_STRAND_PHASE[s];
    const sSpatial = REJECTED_LOAF_STRAND_SPATIAL[s];
    const sAmp = REJECTED_LOAF_STRAND_AMP[s];

    const anchorX = skullX + (s - 2) * (drawSize * 0.075 * squashX);
    const anchorY = skullY + bulbLocalY;
    const strandLength = drawSize * REJECTED_LOAF_STRAND_LEN[s] * stretchY;

    _rejectedStrandPts[s][0].x = anchorX;
    _rejectedStrandPts[s][0].y = anchorY;

    for (let k = 1; k <= 6; k++) {
      const u = k / 6;
      const idleWavePhase = t * (2.2 * sFreq) - u * sSpatial + sPhase;
      const idleWaveX = Math.sin(idleWavePhase) * (u * 5.2 * sAmp) + Math.sin(t * 1.1 + sPhase) * (u * 2.2);
      const idleWaveY = Math.cos(t * (1.6 * sFreq) - u * 3.0 + sPhase) * (u * 2.4 * sAmp);
      const umbrellaSpreadX = (s - 2) * umbrellaFactor * (u * drawSize * 0.18);

      _rejectedStrandPts[s][k].x = anchorX + idleWaveX + umbrellaSpreadX;
      _rejectedStrandPts[s][k].y = anchorY + (u * strandLength) + idleWaveY;
    }
  }

  ctx.save();
  ctx.lineCap = 'round';
  for (let k = 1; k <= 6; k++) {
    const prevIdx = k - 1;
    ctx.strokeStyle = REJECTED_LOAF_SEG_COLORS[k - 1];
    ctx.lineWidth = REJECTED_LOAF_SEG_WIDTHS[k - 1];
    ctx.beginPath();
    for (let s = 0; s < 5; s++) {
      ctx.moveTo(_rejectedStrandPts[s][prevIdx].x, _rejectedStrandPts[s][prevIdx].y);
      ctx.lineTo(_rejectedStrandPts[s][k].x, _rejectedStrandPts[s][k].y);
    }
    ctx.stroke();
  }
  ctx.fillStyle = '#f5d0fe';
  ctx.beginPath();
  for (let s = 0; s < 5; s++) {
    const tip = _rejectedStrandPts[s][6];
    ctx.moveTo(tip.x + 1.4, tip.y);
    ctx.arc(tip.x, tip.y, 1.4, 0, Math.PI * 2);
  }
  ctx.fill();
  ctx.restore();
}

// Sul Looting Vulture: 4-segment horizontally articulated Celestial Dragon
// (Tail at -0.30*segSize, Body at 0, Pyramid at +0.18*segSize, Head at +0.22*segSize)
// with procedural Pelvis Bridge, Throat Ridge, Mane Tufts, Belly Fur, Caudal Fin Tendrils,
// Whisker Barbels, and Solar Apex Star, undulating along a traveling S-wave spine.
function drawLootingVultureMvp(ctx, t, centerX, centerY, segSize, race, team) {
  const tailBmp = buildSpriteBitmap('LootingVulture_Tail', race, team);
  const bodyBmp = buildSpriteBitmap('LootingVulture_Body', race, team);
  const pyramidBmp = buildSpriteBitmap('LootingVulture_Pyramid', race, team);
  const headBmp = buildSpriteBitmap('LootingVulture_Head', race, team);
  if (!tailBmp || !bodyBmp || !pyramidBmp || !headBmp) return;

  const forwardDir = team === 'western' ? 1 : -1;
  const baseY = centerY + segSize * 0.08;
  const spineClock = t * 4.2;

  const waveHead = Math.sin(spineClock) * 2.6;
  const waveNeck = Math.sin(spineClock - 0.85) * 2.2;
  const waveBody = Math.sin(spineClock - 1.95) * 2.4;
  const waveTail = Math.sin(spineClock - 3.60) * 3.8;

  const pitchHead = Math.cos(spineClock) * 0.09 * forwardDir;
  const pitchBody = Math.cos(spineClock - 1.95) * 0.06 * forwardDir;
  const pitchTail = Math.cos(spineClock - 3.60) * 0.14 * forwardDir;

  const tailX = centerX - segSize * 0.30 * forwardDir;
  const tailY = baseY + waveTail;
  const bodyX = centerX;
  const bodyY = baseY + waveBody;
  const pyramidScale = 0.78;
  const pyramidDrawSize = segSize * pyramidScale;
  const pyramidX = centerX + segSize * 0.18 * forwardDir;
  const pyramidY = baseY - segSize * 0.14 + waveNeck * 0.85;
  const headX = centerX + segSize * 0.22 * forwardDir;
  const headY = baseY + waveHead;

  ctx.save();
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';

  // 1. Caudal Tail Spine & Radiant Golden Tail Fin
  ctx.save();
  ctx.translate(tailX, tailY);
  ctx.rotate(pitchTail);
  ctx.drawImage(tailBmp.normal, 0, 0, tailBmp.size, tailBmp.size, -segSize * 0.5, -segSize * 0.5, segSize, segSize);
  // Caudal Fin Ray Tendrils
  const finTipX = -segSize * 0.44 * forwardDir;
  const finTipY = -segSize * 0.14;
  for (let f = 0; f < 3; f++) {
    const fWave = Math.sin(spineClock * 1.4 - 4.2 + f * 1.3) * 2.4;
    const rLen = segSize * (f === 1 ? 0.14 : 0.10);
    const fy = finTipY + (f - 1) * (segSize * 0.055);
    ctx.strokeStyle = f === 1 ? '#fef08a' : '#eab308';
    ctx.lineWidth = f === 1 ? 1.8 : 1.3;
    ctx.beginPath();
    ctx.moveTo(finTipX, fy);
    ctx.quadraticCurveTo(
      finTipX - rLen * 0.5 * forwardDir,
      fy + fWave * 0.5,
      finTipX - rLen * forwardDir,
      fy + fWave
    );
    ctx.stroke();
  }
  ctx.restore();

  // 1b. Procedural Pelvis & Tail Spine Bridge
  const pStartX = bodyX - segSize * 0.28 * forwardDir;
  const pStartY = bodyY - segSize * 0.04;
  const pEndX = tailX + segSize * 0.02 * forwardDir;
  const pEndY = tailY - segSize * 0.05;
  const pCtrlX = (pStartX + pEndX) * 0.5;
  const pCtrlY = (pStartY + pEndY) * 0.5 + Math.sin(spineClock - 2.75) * 1.8;

  ctx.strokeStyle = '#05070b';
  ctx.lineWidth = Math.max(4, segSize * 0.11);
  ctx.beginPath();
  ctx.moveTo(pStartX, pStartY);
  ctx.quadraticCurveTo(pCtrlX, pCtrlY, pEndX, pEndY);
  ctx.stroke();

  ctx.strokeStyle = '#ca8a04';
  ctx.lineWidth = Math.max(2.5, segSize * 0.07);
  ctx.beginPath();
  ctx.moveTo(pStartX, pStartY);
  ctx.quadraticCurveTo(pCtrlX, pCtrlY, pEndX, pEndY);
  ctx.stroke();

  ctx.strokeStyle = '#fde047';
  ctx.lineWidth = Math.max(1.4, segSize * 0.035);
  ctx.beginPath();
  ctx.moveTo(pStartX, pStartY - 0.8);
  ctx.quadraticCurveTo(pCtrlX, pCtrlY - 0.8, pEndX, pEndY - 0.8);
  ctx.stroke();

  // 2. Serpentine Body (Thoracic Belly Dip & Pelvic Arch)
  ctx.save();
  ctx.translate(bodyX, bodyY);
  ctx.rotate(pitchBody);
  ctx.drawImage(bodyBmp.normal, 0, 0, bodyBmp.size, bodyBmp.size, -segSize * 0.5, -segSize * 0.5, segSize, segSize);
  ctx.restore();

  // 2b. Procedural Dorsal Mane Tufts & Ventral Belly Fur Fringes
  for (let m = 0; m < 6; m++) {
    const mFrac = m / 5;
    const mx = bodyX + (0.26 - mFrac * 0.54) * segSize * forwardDir;
    const my = bodyY - segSize * (0.18 - Math.sin(mFrac * Math.PI) * 0.06);
    const mFlick = Math.sin(spineClock * 1.3 - m * 0.9) * 1.5;
    ctx.strokeStyle = m % 2 === 0 ? '#fde047' : '#ca8a04';
    ctx.lineWidth = 1.6;
    ctx.beginPath();
    ctx.moveTo(mx, my);
    ctx.lineTo(mx - (2.8 + mFlick * 0.4) * forwardDir, my - 3.6 + mFlick * 0.3);
    ctx.stroke();

    const bx = bodyX + (0.16 - mFrac * 0.42) * segSize * forwardDir;
    const by = bodyY + segSize * (0.14 + Math.sin(mFrac * Math.PI) * 0.13);
    ctx.strokeStyle = m % 2 === 0 ? '#ca8a04' : '#eab308';
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.moveTo(bx, by);
    ctx.lineTo(bx - (2.2 - mFlick * 0.4) * forwardDir, by + 3.4 + mFlick * 0.3);
    ctx.stroke();
  }

  // 2c. Procedural Ventral Throat Ridge
  const tStartX = bodyX + segSize * 0.22 * forwardDir;
  const tStartY = bodyY + segSize * 0.01;
  const tEndX = headX + segSize * 0.02 * forwardDir;
  const tEndY = headY + segSize * 0.03;
  const tCtrlX = (tStartX + tEndX) * 0.5;
  const tCtrlY = (tStartY + tEndY) * 0.5 + segSize * 0.03;

  ctx.strokeStyle = '#05070b';
  ctx.lineWidth = Math.max(3.5, segSize * 0.09);
  ctx.beginPath();
  ctx.moveTo(tStartX, tStartY);
  ctx.quadraticCurveTo(tCtrlX, tCtrlY, tEndX, tEndY);
  ctx.stroke();

  ctx.strokeStyle = '#ca8a04';
  ctx.lineWidth = Math.max(2, segSize * 0.055);
  ctx.beginPath();
  ctx.moveTo(tStartX, tStartY);
  ctx.quadraticCurveTo(tCtrlX, tCtrlY, tEndX, tEndY);
  ctx.stroke();

  // 3. 3D Sandstone / Terracotta Pyramid (Cervical Neck Crest)
  ctx.save();
  ctx.translate(pyramidX, pyramidY);
  ctx.rotate(pitchHead * 0.45);
  ctx.drawImage(
    pyramidBmp.normal,
    0, 0, pyramidBmp.size, pyramidBmp.size,
    -pyramidDrawSize * 0.5, -pyramidDrawSize * 0.5,
    pyramidDrawSize, pyramidDrawSize
  );
  ctx.restore();

  // 4. Autonomous Draconic Head (Horn, 6 Golden Compound Eyes, Fangs)
  ctx.save();
  ctx.translate(headX, headY);
  ctx.rotate(pitchHead);
  ctx.drawImage(headBmp.normal, 0, 0, headBmp.size, headBmp.size, -segSize * 0.5, -segSize * 0.5, segSize, segSize);

  // Flowing Golden Dragon Whiskers
  for (let w = 0; w < 2; w++) {
    const wStartX = segSize * (0.26 - w * 0.05) * forwardDir;
    const wStartY = segSize * (0.04 + w * 0.06);
    const wLen = segSize * (0.18 + w * 0.04);
    const wWave = Math.sin(spineClock * 1.5 - w * 1.4) * 2.5;
    ctx.strokeStyle = w === 0 ? '#fef08a' : '#facc15';
    ctx.lineWidth = w === 0 ? 1.6 : 1.3;
    ctx.beginPath();
    ctx.moveTo(wStartX, wStartY);
    ctx.quadraticCurveTo(
      wStartX + wLen * 0.5 * forwardDir,
      wStartY + wWave * 0.6,
      wStartX + wLen * forwardDir,
      wStartY + 1.8 + wWave
    );
    ctx.stroke();
  }
  ctx.restore();

  // 5. Radiant Solar Apex Star on Pyramid Capstone
  const apexPx = pyramidX + pyramidDrawSize * 0.04 * forwardDir;
  const apexPy = pyramidY - pyramidDrawSize * 0.44;
  const flareR = (4.5 + Math.sin(t * 7.5) * 1.5) * (segSize / 56);
  ctx.strokeStyle = 'rgba(254, 240, 138, 0.92)';
  ctx.lineWidth = 1.4;
  ctx.beginPath();
  ctx.moveTo(apexPx - flareR, apexPy);
  ctx.lineTo(apexPx + flareR, apexPy);
  ctx.moveTo(apexPx, apexPy - flareR);
  ctx.lineTo(apexPx, apexPy + flareR);
  ctx.stroke();
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(apexPx - 1, apexPy - 1, 2, 2);

  ctx.restore();
}

function spawnSparkleBurst(particles, x, y) {
  for (let i = 0; i < 16; i++) {
    const angle = Math.random() * Math.PI * 2;
    const speed = 28 + Math.random() * 70;
    particles.push({
      x, y,
      vx: Math.cos(angle) * speed,
      vy: Math.sin(angle) * speed - 20,
      age: 0,
      life: 0.45 + Math.random() * 0.25,
      size: 1.4 + Math.random() * 2.2
    });
  }
}

// canvas must already have its pixel width/height set (e.g. from
// getBoundingClientRect()) by the caller - this module only draws.
export function createUnitMvpSpotlight(canvas, mvp) {
  if (!canvas || !mvp) return { stop() {} };

  const ctx = canvas.getContext('2d');
  ctx.imageSmoothingEnabled = false;

  const width = canvas.width;
  const height = canvas.height;
  const race = mvp.cardData?.race || (mvp.unitType?.includes('Sul') || ['Pulp', 'AntiShade', 'RejectedLoaf', 'LootingVulture', 'ParasiteVulture', 'EnforcerVulture'].includes(mvp.unitType) ? 'Sul' : 'Human');
  const walkSet = INFANTRY_WALK[mvp.unitType] || null;
  const isRejectedLoaf = mvp.unitType === 'RejectedLoaf' || mvp.unitType === 'Rejected_Loaf' || mvp.unitType === 'Rejected Loaf';
  const isLootingVulture = mvp.unitType === 'LootingVulture' || mvp.unitType === 'Looting_Vulture' || mvp.unitType === 'Looting Vulture';
  const isPinkBeret = mvp.unitType === 'PinkBeret' || mvp.unitType === 'Pink Beret' || mvp.cardData?.name === 'Pink Beret';
  const isPulp = mvp.unitType === 'Pulp' || mvp.cardData?.name === 'Pulp';
  const singleBitmap = (walkSet || isLootingVulture || isPinkBeret || isPulp) ? null : buildSpriteBitmap(mvp.unitType, race, mvp.team);
  const drawSize = Math.min(height * 0.82, MAX_DRAW_SIZE);

  // Matches the real battlefield convention: western starts on the left
  // side and faces/moves right (toward the enemy), eastern the mirror -
  // buildSpriteBitmap() already bakes the team-facing mirror into the
  // bitmap itself, so no extra flip is needed here.
  const movingRight = mvp.team === 'western';
  const startMargin = isLootingVulture ? drawSize * 1.15 : drawSize;
  const startX = movingRight ? -startMargin : width + startMargin;
  const centerX = width / 2;

  let phase = 'in'; // 'in' -> 'idle' (forever, until stop())
  let x = startX;
  let idleT = 0;
  let idleSparklesFired = 0;
  let walkClock = 0;
  const particles = [];
  let rafId = null;
  let lastT = null;
  let stopped = false;
  let visible = true;   // flipped by the IntersectionObserver below; true until it reports
  let dirty = true;     // the last frame drawn is out of date (first frame, or activity just ended)
  let observer = null;

  function updateParticles(dt) {
    for (let i = particles.length - 1; i >= 0; i--) {
      const p = particles[i];
      p.age += dt;
      if (p.age >= p.life) { particles.splice(i, 1); continue; }
      p.x += p.vx * dt;
      p.y += p.vy * dt;
      p.vy += 60 * dt;
    }
  }

  function drawParticles() {
    for (const p of particles) {
      const lifeFrac = Math.max(0, 1 - p.age / p.life);
      const pr = p.size * lifeFrac;
      // Outer golden halo (0ms GPU blur cost, visually identical)
      ctx.globalAlpha = lifeFrac * 0.4;
      ctx.fillStyle = '#ffcc00';
      ctx.beginPath();
      ctx.arc(p.x, p.y, pr * 2.0, 0, Math.PI * 2);
      ctx.fill();
      // Inner bright spark core
      ctx.globalAlpha = lifeFrac;
      ctx.fillStyle = '#fff3c4';
      ctx.beginPath();
      ctx.arc(p.x, p.y, pr, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.globalAlpha = 1;
  }

  // isWalking selects the leg-frame (infantry) or bob (fallback); drawX/drawY
  // is the box every layer is stacked into.
  function drawUnit(isWalking, drawX, drawY) {
    if (isPinkBeret) {
      const isGold = (mvp.cardData?.spriteRecolor === 'gold' || mvp.edition === 'gold');
      const isClassic = !!(mvp.forceHD || mvp.variant === 'classic');
      const prefix = isClassic
        ? (isGold ? 'PinkBeretGold_Classic_' : 'PinkBeret_Classic_')
        : (isGold ? 'PinkBeretGold_UHD_' : 'PinkBeret_UHD_');
      const pbDrawSize = Math.round(drawSize * (isClassic ? 1.12 : 1.20));
      const pbX = drawX - (pbDrawSize - drawSize) / 2;
      const pbY = drawY - (pbDrawSize - drawSize);

      if (isWalking) {
        const cycle = ((walkClock * 16) % 32 + 32) % 32;
        const frameNum = Math.floor(cycle) + 1;
        const bmp = buildSpriteBitmap(prefix + 'Walk_Armed' + frameNum, race, mvp.team);
        if (bmp?.normal) {
          ctx.drawImage(bmp.normal, 0, 0, bmp.size, bmp.size, pbX, pbY, pbDrawSize, pbDrawSize);
        }
      } else {
        const bmp = buildSpriteBitmap(prefix + 'Stand', race, mvp.team);
        if (bmp?.normal) {
          ctx.drawImage(bmp.normal, 0, 0, bmp.size, bmp.size, pbX, pbY, pbDrawSize, pbDrawSize);
        }
      }
    } else if (walkSet) {
      const cycle = ((walkClock * WALK_CYCLE_SPEED) % (Math.PI * 2) + Math.PI * 2) % (Math.PI * 2);
      const phaseIdx = Math.floor((cycle / (Math.PI * 2)) * walkSet.walk.length);
      const legsKey = isWalking ? walkSet.walk[phaseIdx] : walkSet.stand;
      const legs = buildSpriteBitmap(legsKey, race, mvp.team);
      const torso = buildSpriteBitmap(walkSet.torso, race, mvp.team);
      const gun = buildSpriteBitmap(walkSet.gun, race, mvp.team);
      ctx.drawImage(legs.normal, 0, 0, legs.size, legs.size, drawX, drawY, drawSize, drawSize);
      ctx.drawImage(torso.normal, 0, 0, torso.size, torso.size, drawX, drawY, drawSize, drawSize);
      ctx.drawImage(gun.normal, 0, 0, gun.size, gun.size, drawX, drawY, drawSize, drawSize);
    } else if (isLootingVulture) {
      drawLootingVultureMvp(ctx, walkClock, drawX + drawSize / 2, drawY + drawSize / 2, drawSize, race || 'Sul', mvp.team);
    } else if (isPulp) {
      const cycle = ((walkClock * WALK_CYCLE_SPEED) % (Math.PI * 2) + Math.PI * 2) % (Math.PI * 2);
      const walkFrames = ['Pulp_Legs_Walk1', 'Pulp_Legs_Walk2', 'Pulp_Legs_Walk3', 'Pulp_Legs_Walk4', 'Pulp_Legs_Walk5', 'Pulp_Legs_Walk6'];
      const phaseIdx = Math.floor((cycle / (Math.PI * 2)) * walkFrames.length);
      const legsKey = isWalking ? walkFrames[phaseIdx] : 'Pulp_Legs_Stand';

      const weapon = buildSpriteBitmap('Pulp_Weapon', race, mvp.team);
      const legs = buildSpriteBitmap(legsKey, race, mvp.team);
      const body = buildSpriteBitmap('Pulp_Body', race, mvp.team);
      const head = buildSpriteBitmap('Pulp_Head', race, mvp.team);

      if (weapon?.normal && legs?.normal && body?.normal && head?.normal) {
        ctx.drawImage(weapon.normal, 0, 0, weapon.size, weapon.size, drawX, drawY, drawSize, drawSize);
        ctx.drawImage(legs.normal, 0, 0, legs.size, legs.size, drawX, drawY, drawSize, drawSize);
        ctx.drawImage(body.normal, 0, 0, body.size, body.size, drawX, drawY, drawSize, drawSize);
        ctx.drawImage(head.normal, 0, 0, head.size, head.size, drawX, drawY, drawSize, drawSize);
      } else {
        const fallbackBmp = buildSpriteBitmap('Pulp', race, mvp.team) || singleBitmap;
        if (fallbackBmp?.normal) {
          ctx.drawImage(fallbackBmp.normal, 0, 0, fallbackBmp.size, fallbackBmp.size, drawX, drawY, drawSize, drawSize);
        }
      }
    } else {
      const bob = isWalking ? Math.sin(walkClock * BOB_SPEED) * BOB_AMPLITUDE : 0;
      if (isRejectedLoaf) {
        drawRejectedLoafFilaments(ctx, walkClock, drawX + drawSize / 2, drawY + bob + drawSize / 2, drawSize);
      }
      if (singleBitmap?.normal) {
        ctx.drawImage(singleBitmap.normal, 0, 0, singleBitmap.size, singleBitmap.size, drawX, drawY + bob, drawSize, drawSize);
      }
    }
  }

  function step(dt) {
    walkClock += dt;

    if (phase === 'in') {
      x += (movingRight ? 1 : -1) * WALK_SPEED * dt;
      if ((movingRight && x >= centerX) || (!movingRight && x <= centerX)) {
        x = centerX;
        phase = 'idle';
        idleT = 0;
        idleSparklesFired = 0;
      }
    } else if (phase === 'idle') {
      idleT += dt;
      if (idleSparklesFired < IDLE_SPARKLE_AT.length && idleT >= IDLE_SPARKLE_AT[idleSparklesFired]) {
        const sparkX = isLootingVulture ? (x + drawSize * 0.18 * (movingRight ? 1 : -1)) : x;
        const sparkY = isLootingVulture ? (height / 2 - drawSize * 0.34) : (height / 2 - drawSize * 0.15);
        spawnSparkleBurst(particles, sparkX, sparkY);
        idleSparklesFired++;
      }
      if (idleT >= IDLE_CYCLE_SECONDS) {
        idleT = 0;
        idleSparklesFired = 0;
      }
    }

    updateParticles(dt);

    // Walking, sparkles in flight, Rejected Loaf's waving filaments, and Looting Vulture's
    // undulating serpentine dragon spine change every frame; a standing unit does not.
    const animating = phase === 'in' || particles.length > 0 || isRejectedLoaf || isLootingVulture || isPulp;
    if (animating || dirty) {
      ctx.clearRect(0, 0, width, height);
      drawUnit(phase === 'in', x - drawSize / 2, height / 2 - drawSize / 2);
      drawParticles();
      dirty = animating;
    }

    return true; // runs forever until stop() cancels the rAF loop
  }

  function frame(now) {
    if (stopped || !visible) { rafId = null; return; }
    rafId = requestAnimationFrame(frame);
    // 120 Hz phones: a 60 fps cap is plenty for a decorative walk-in (same throttle as the Cargo Bay VFX).
    if (lastT !== null && now - lastT < 15) return;
    if (lastT === null) lastT = now;
    const dt = Math.min(0.05, (now - lastT) / 1000);
    lastT = now;
    step(dt);
  }
  rafId = requestAnimationFrame(frame);

  // Sleep while the canvas is off screen (display:none modal, other menu on top), wake when it returns.
  if (typeof IntersectionObserver === 'function') {
    observer = new IntersectionObserver((entries) => {
      const last = entries[entries.length - 1];
      if (!last || stopped) return;
      visible = last.isIntersecting;
      if (visible && rafId === null) {
        lastT = null; // do not count the time asleep as one giant frame
        dirty = true; // the canvas may have been cleared/lost while hidden
        rafId = requestAnimationFrame(frame);
      } else if (!visible && rafId !== null) {
        cancelAnimationFrame(rafId);
        rafId = null;
      }
    });
    observer.observe(canvas);
  }

  return {
    stop() {
      stopped = true;
      if (rafId) cancelAnimationFrame(rafId);
      rafId = null;
      observer?.disconnect();
      observer = null;
      ctx.clearRect(0, 0, width, height);
    }
  };
}
